/* assets/microscope-service-form.js
   Submit handler + conversion tracking for the Microscope Service request form.

   Deliberately separate from contact-form-submit.js so the consultation and
   contact forms are untouched. Differences from that handler:
     - reports success and failure in the UI instead of only the console
     - keeps the lead recoverable: if the webhook fails, the form falls back to
       a normal Shopify contact-form post rather than dropping the submission
     - scoped by element, so the id does not have to be "ContactForm"

   Conversion tracking fires on BOTH success paths:
     1. webhook succeeded  -> in-page, no reload
     2. webhook failed     -> native Shopify post, page reloads with the
                              confirmation rendered by Liquid
   Path 2 is deduplicated per URL so a refresh of ?contact_posted=true does not
   bill a second conversion.
*/

function msServiceFireConversion(cfg, detail, done) {
  var finished = false;
  function finish() {
    if (finished) return;
    finished = true;
    if (done) done();
  }

  // 1. dataLayer event — the single hook for Google Tag Manager. Conversion
  //    tags (Google Ads, Meta, LinkedIn) are configured in GTM against this
  //    event, so no vendor IDs live in the theme.
  try {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: cfg.dataLayerEvent || 'microscope_service_lead' };
    for (var k in detail) {
      if (Object.prototype.hasOwnProperty.call(detail, k)) payload[k] = detail[k];
    }
    if (done) {
      // GTM calls eventCallback once every tag bound to this event has fired,
      // or after eventTimeout — so a redirect cannot outrun the tags.
      payload.eventCallback = finish;
      payload.eventTimeout = 1200;
    }
    window.dataLayer.push(payload);
  } catch (e) {
    console.error('[microscope-service] dataLayer push failed:', e);
  }

  // 2. DOM event — lets any non-GTM script subscribe without editing this file.
  try {
    document.dispatchEvent(new CustomEvent('microscope-service:lead', { detail: detail }));
  } catch (e) {}

  // Safety net: if GTM is blocked or absent, eventCallback never runs.
  if (done) {
    setTimeout(finish, 1300);
  } else {
    finish();
  }
}

function initMicroscopeServiceForm(options) {
  var cfg = options.tracking || {};

  // --- Path 2: the page rendered the native post-back confirmation ---------
  var posted = document.getElementById(options.postedId);
  if (posted) {
    var key = 'msLeadFired:' + window.location.pathname + window.location.search;
    var alreadyFired = false;
    try {
      alreadyFired = window.sessionStorage.getItem(key) === '1';
    } catch (e) {}
    if (!alreadyFired) {
      try {
        window.sessionStorage.setItem(key, '1');
      } catch (e) {}
      msServiceFireConversion(cfg, { form_source: 'microscope-service', delivery: 'shopify-contact' });
    }
    return;
  }

  // --- Path 1: the in-page AJAX form --------------------------------------
  var form = document.getElementById(options.formId);
  if (!form) return;

  var card = document.getElementById(options.cardId);
  var successPanel = document.getElementById(options.successId);
  var errorPanel = document.getElementById(options.errorId);
  var button = form.querySelector('[type="submit"]');
  var buttonLabel = button ? button.textContent : '';
  var webhookUrl = options.webhookUrl;

  function showError(message) {
    if (!errorPanel) return;
    errorPanel.textContent = message;
    errorPanel.hidden = false;
  }

  function hideError() {
    if (errorPanel) errorPanel.hidden = true;
  }

  function setBusy(busy) {
    if (!button) return;
    button.disabled = busy;
    button.textContent = busy ? 'Sending…' : buttonLabel;
  }

  function showSuccess() {
    if (!successPanel) return;
    form.hidden = true;
    successPanel.hidden = false;
    // Move focus to the confirmation so screen readers announce it.
    successPanel.setAttribute('tabindex', '-1');
    successPanel.focus();
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function resetForm() {
    form.reset();
    hideError();
    setBusy(false);
    if (successPanel) successPanel.hidden = true;
    form.hidden = false;
    var firstField = form.querySelector('input, select, textarea');
    if (firstField) firstField.focus();
  }

  var resetButton = successPanel ? successPanel.querySelector('[data-ms-reset]') : null;
  if (resetButton) {
    resetButton.addEventListener('click', function (event) {
      event.preventDefault();
      resetForm();
    });
  }

  function onDelivered(data) {
    setBusy(false);
    var detail = {
      form_source: 'microscope-service',
      delivery: 'webhook',
      service_needed: data['contact[service_needed]'] || '',
      manufacturer_and_model: data['contact[manufacturer_and_model]'] || ''
    };
    if (cfg.mode === 'redirect' && cfg.redirectUrl) {
      // Fire first, navigate once the tag has reported (or timed out).
      msServiceFireConversion(cfg, detail, function () {
        window.location.assign(cfg.redirectUrl);
      });
      return;
    }
    msServiceFireConversion(cfg, detail);
    showSuccess();
  }

  form.addEventListener('submit', function (event) {
    // Let the browser surface its own messages on required/invalid fields.
    if (!form.checkValidity()) return;

    // No webhook configured: fall through to the native Shopify contact post.
    if (!webhookUrl) return;

    event.preventDefault();

    // Honeypot filled: almost certainly a bot. Pretend it worked, send nothing,
    // and fire no conversion.
    var honeypot = form.elements.ms_hp_website;
    if (honeypot && honeypot.value) {
      showSuccess();
      return;
    }

    hideError();
    setBusy(true);

    var data = {};
    new FormData(form).forEach(function (value, key) {
      if (key !== 'ms_hp_website') data[key] = value;
    });
    data.form_source = 'microscope-service';
    data.submitted_at = new Date().toISOString();
    data.page_url = window.location.href;

    fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Webhook responded ' + response.status);
        onDelivered(data);
      })
      .catch(function (error) {
        console.error('[microscope-service] webhook failed, falling back to contact post:', error);
        // Do not lose the lead: submit natively so it still reaches the shop
        // inbox. The conversion then fires on the reloaded confirmation.
        showError('Sending your request…');
        form.submit();
      });
  });
}
